import React, { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import ReactMarkdown from 'react-markdown';
import MagneticButton from '../Miscellaneous/MagneticButton';
import { fetchAPI } from '../../utils/api';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const AboutOne = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getData = async () => {
      try {
        // Fetch about data with population for all fields including blocks
        const response = await fetchAPI('/about', {
          populate: {
            blocks: {
              populate: '*'
            }
          }
        });
        if (response.data) {
          setData(response.data);
        }
      } catch (error) {
        console.error("Error fetching about data:", error);
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    if (loading || !data) return;

    // Delay animations to ensure DOM is ready
    const timer = setTimeout(() => {
      const splitElements = document.querySelectorAll(".reveal-text");

      if (splitElements.length === 0) return;

      splitElements.forEach((element) => {
        // Only verify if element is visible and has content before trying to split
        if (element.textContent.trim().length > 0) {
          const text = new SplitType(element, { types: 'words, chars' });
          gsap.fromTo(
            text.chars,
            { color: "#B6BCC6" },
            {
              color: "#030712",
              scrollTrigger: {
                trigger: element,
                start: 'top 85%',
                end: 'top 20%',
                scrub: true,
              },
              stagger: 0.05,
            }
          );
        }
      });

      ScrollTrigger.refresh();
    }, 300);

    return () => {
      clearTimeout(timer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [loading, data]);

  if (loading) return <div>Loading...</div>;
  if (!data) return null;

  // Extract fields (handling potential casing differences or missing fields)
  // Strapi standard is camelCase for fields defined in admin
  const { Mission, Vision, Governance, Programs, blocks, title } = data;

  const missionContent = Mission || data.mission;
  const visionContent = Vision || data.vision;
  const governanceContent = Governance || data.governance;
  const programsContent = Programs || data.programs;

  // Render helper for markdown content
  const renderMarkdownSection = (title, content) => {
    if (!content) return null;
    return (
      <div className="markdown-section mb-5">
        {title && <h3 className="section-title mb-4 reveal-text">{title}</h3>}
        <div className="rich-text-content">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      <div className="row py-5 my-5">
        <div className="col-12">
          {/* Main Title */}
          <div className="content mb-5">
            <h2 className="title reveal-text mt-0">{title}</h2>
          </div>

          {/* Dynamic Zone Blocks (Legacy/Flexibility) */}
          {blocks?.map((block, index) => {
            if (block.__component === 'shared.rich-text') {
              return (
                <div key={index} className="rich-text-content mb-5" dangerouslySetInnerHTML={{ __html: block.body }} />
              );
            }
            return null;
          })}

          {/* Mission & Vision Grid */}
          <div className="row mb-4">
            <div className="col-md-6">
              {renderMarkdownSection("Our Mission", missionContent)}
            </div>
            <div className="col-md-6">
              {renderMarkdownSection("Our Vision", visionContent)}
            </div>
          </div>

          {/* Governance */}
          <div className="row mb-4">
            <div className="col-12">
              {renderMarkdownSection("Governance", governanceContent)}
            </div>
          </div>

          {/* Programs */}
          <div className="row mb-5">
            <div className="col-12">
              {renderMarkdownSection("Programs", programsContent)}
            </div>
          </div>

          <div className="wrapper mt-4">
            <MagneticButton
              href="/about"
              className="btn-outline"
            >
              More About Us
            </MagneticButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutOne;
